import { z } from 'zod';

export const EDLINK_ISSUER = 'https://ed.link/api/authentication/oidc';

export const userIdentityStatusSchema = z.enum(['active', 'inactive', 'registrable']);
export type UserIdentityStatus = z.infer<typeof userIdentityStatusSchema>;
export const UserIdentityStatus = userIdentityStatusSchema.enum;

export const GOOGLE_ISS_PREFIX = 'https://securetoken.google.com/';

export function isGoogleIss(iss: string): boolean {
  return iss.startsWith(GOOGLE_ISS_PREFIX);
}

export const userIdentityApiSchema = z.object({
  id: z.string(),
  iss: z.string(),
  sub: z.string().nullable(),
  status: userIdentityStatusSchema,
});
export type UserIdentityApi = z.infer<typeof userIdentityApiSchema>;

export function userIdentityStatusLabel(status: UserIdentityStatus): string {
  switch (status) {
    case UserIdentityStatus.active:
      return 'Active';
    case UserIdentityStatus.inactive:
      return 'Inactive';
    case UserIdentityStatus.registrable:
      return 'Registrable';
  }
}

export const allUserIdentityStatuses = [
  UserIdentityStatus.active,
  UserIdentityStatus.inactive,
  UserIdentityStatus.registrable,
] as const;

const nullableTrimmedOptionalString = z
  .string()
  .nullable()
  .optional()
  .transform((val) => val?.trim() || null);

export const userIdentityProviderUpdateSchema = z.object({
  status: userIdentityStatusSchema,
  iss: nullableTrimmedOptionalString,
  sub: nullableTrimmedOptionalString,
});
export type UserIdentityProviderUpdate = z.infer<typeof userIdentityProviderUpdateSchema>;

export const userIdentityProviderKeySchema = z.enum(['google', 'edlink']);
export type UserIdentityProviderKey = z.infer<typeof userIdentityProviderKeySchema>;
export const UserIdentityProviderKey = userIdentityProviderKeySchema.enum;

export const updateUserIdentitiesOptionsSchema = z.object({
  google: userIdentityProviderUpdateSchema,
  edlink: userIdentityProviderUpdateSchema,
});
export type UpdateUserIdentitiesOptions = z.infer<typeof updateUserIdentitiesOptionsSchema>;

export type UserIdentityProviderView = {
  status: UserIdentityStatus;
  iss: string | null;
  sub: string | null;
};

export function userIdentityProviderKeyLabel(providerKey: UserIdentityProviderKey): string {
  switch (providerKey) {
    case UserIdentityProviderKey.google:
      return 'Google Workspace';
    case UserIdentityProviderKey.edlink:
      return 'Edlink';
  }
}

export function findGoogleIdentity(identities: UserIdentityApi[]): UserIdentityApi | undefined {
  return identities.find((identity) => isGoogleIss(identity.iss));
}

export function findEdlinkIdentity(identities: UserIdentityApi[]): UserIdentityApi | undefined {
  return identities.find((identity) => identity.iss === EDLINK_ISSUER);
}
